import { useChat } from '@ai-sdk/react';
import { useMemo, useRef, useEffect, useState } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  mergeMessagesWithBusyFollowUps,
  forgetPersistedBusyCopilotFollowUps,
  clearBusyCopilotFollowUps,
  clearUserMessagePlacements,
  readUserMessagePlacements,
} from '../store/copilot-pending-follow-up';
import {
  useChatInstancesStore,
  resolveCopilotTargetFields,
} from '../store/chatInstancesStore';
import { useTargetType } from '../store/projectStore';
import type {
  ChatRef,
  SendBranchInlineEdit,
  MentionEntityWithOffset,
} from '@terra/ui-design-system';
import { useMentions } from './useMentions';
import { useChatTabs } from '../store/chatTabsStore';
import { v4 as uuidv4 } from 'uuid';
import {
  hasChatActivity,
  shouldAttemptStreamResume,
} from '@terra/shared-utils';
import { useTranslations } from 'next-intl';
import { getPendingHITLReview } from '../utils/chatHitlReviewState';
import { useCopilotChatBranching } from './useCopilotChatBranching';
import { isActiveChatInstanceStatus } from '../utils/chatNetworkErrors';
import {
  useChatInstanceInitialHistory,
  useChatInstanceHistoryPagination,
} from './useChatInstanceHistory';
import { useChatInstanceQueueDrain } from './useChatInstanceQueueDrain';
import { performChatInstanceSendMessage } from './useChatInstanceSendMessage';
import { useChatInstanceStreamActions } from './useChatInstanceStreamActions';

const FORCE_RENDER_THROTTLE_MS = 50;

type TaskContext = {
  applicationId: string | null;
  applicationUrl?: string | null;
};

type UseChatInstanceParams = {
  chatId: string;
  conversationId: string | null;
  targetId: string | undefined;
  taskContextRef: React.RefObject<TaskContext | undefined>;
  getChatExecuteUrl: () => string;
  chatComponentRef?: React.RefObject<ChatRef | null>;
  editingQueuedMessageId?: string | null;
  onQueuedMessageEditComplete?: () => void;
  skillDraftId?: string;
  linkedObject?: {
    kind: string;
    id: string;
    label?: string;
  };
};

export function hasActiveInMemoryChatSession(chatId: string): boolean {
  const chat = useChatInstancesStore.getState().chats.get(chatId);
  const chatStatus = useChatInstancesStore.getState().getChatStatus(chatId);

  return (
    hasChatActivity(chat?.messages) || isActiveChatInstanceStatus(chatStatus)
  );
}

export function useChatInstance({
  chatId,
  targetId,
  conversationId,
  taskContextRef,
  getChatExecuteUrl,
  chatComponentRef,
  editingQueuedMessageId,
  onQueuedMessageEditComplete,
  skillDraftId,
  linkedObject,
}: UseChatInstanceParams) {
  const targetType = useTargetType();
  const tCopilot = useTranslations('chat.copilot');
  const { selectedMentions, clearMentions, getMentionsFromEditor } =
    useMentions();
  const { getOrCreateChat, updateConfig, enqueueMessage, updateQueuedMessage } =
    useChatInstancesStore();

  const intentionalAbortRef = useRef(false);
  const pauseQueueDrainAfterAbortRef = useRef(false);

  const { activeChatTab, updateChatTab } = useChatTabs();
  const isChatWindowActive = activeChatTab?.chatId === chatId;

  const taskContextRefLatest = useRef(taskContextRef);

  taskContextRefLatest.current = taskContextRef;

  const getChatExecuteUrlRef = useRef(getChatExecuteUrl);

  getChatExecuteUrlRef.current = getChatExecuteUrl;

  const targetIdRef = useRef(targetId);

  targetIdRef.current = targetId;

  const conversationIdRef = useRef(conversationId);

  conversationIdRef.current = conversationId;

  const skillDraftIdRef = useRef(skillDraftId);

  skillDraftIdRef.current = skillDraftId;

  const linkedObjectRef = useRef(linkedObject);

  linkedObjectRef.current = linkedObject;

  useEffect(() => {
    const configs = useChatInstancesStore.getState().configs;

    if (configs.has(chatId)) {
      updateConfig(chatId, {
        chatId,
        ...resolveCopilotTargetFields({ targetId, targetType }),
        conversationId,
        taskContextRef: taskContextRefLatest.current,
        getChatExecuteUrl: getChatExecuteUrlRef.current,
        skillDraftId,
      });
    }
  }, [
    chatId,
    targetId,
    targetType,
    conversationId,
    skillDraftId,
    updateConfig,
  ]);

  const {
    initialMessages,
    isLoadingHistory,
    isLoadingHistoryPagination,
    suppressInitialHistoryMerge,
    historyLoadError,
    retryHistoryLoad,
    storedPagination,
    setHistoryPagination,
  } = useChatInstanceInitialHistory({
    chatId,
    conversationId,
    targetId,
  });

  const chat = useMemo(() => {
    if (initialMessages === null) {
      return undefined;
    }

    return getOrCreateChat({
      chatId,
      ...resolveCopilotTargetFields({
        targetId: targetIdRef.current,
        targetType,
      }),
      conversationId: conversationIdRef.current,
      initialMessages,
      taskContextRef: taskContextRefLatest.current,
      getChatExecuteUrl: getChatExecuteUrlRef.current,
      skillDraftId: skillDraftIdRef.current,
      intentionalAbortRef,
    });
  }, [chatId, getOrCreateChat, initialMessages, targetType]);

  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!chat) return;
    const unsubscribe = chat['~registerMessagesCallback'](() => {
      forceRender((c) => c + 1);
    }, FORCE_RENDER_THROTTLE_MS);

    return unsubscribe;
  }, [chat]);

  const resumeDecisionChatIdRef = useRef<string | null>(null);
  const shouldResumeRef = useRef(false);

  if (chat && resumeDecisionChatIdRef.current !== chat.id) {
    resumeDecisionChatIdRef.current = chat.id;
    shouldResumeRef.current = shouldAttemptStreamResume({
      conversationId: conversationIdRef.current,
      messages: chat.messages,
      status: chat.status,
    });
  }

  const {
    messages: chatMessages,
    sendMessage: sendChatMessage,
    error,
    regenerate,
    resumeStream,
    stop,
    status,
    setMessages,
  } = useChat<TerraUIMessage>(
    chat
      ? {
          id: chat.id,
          chat,
          experimental_throttle: FORCE_RENDER_THROTTLE_MS,
          resume: shouldResumeRef.current,
        }
      : {
          id: chatId,
          messages: [],
          experimental_throttle: FORCE_RENDER_THROTTLE_MS,
          resume: false,
        },
  );
  const [busyFollowUps, setBusyFollowUps] = useState<TerraUIMessage[]>([]);
  const displayedMessages = mergeMessagesWithBusyFollowUps({
    messages: chatMessages,
    followUps: busyFollowUps,
    predecessorIdsByMessageId: readUserMessagePlacements(chatId),
  });

  useEffect(() => {
    const persistedIds = new Set(chatMessages.map((message) => message.id));

    forgetPersistedBusyCopilotFollowUps({ chatId, persistedIds });
    setBusyFollowUps((current) => {
      const next = current.filter((message) => !persistedIds.has(message.id));

      return next.length === current.length ? current : next;
    });
  }, [chatId, chatMessages]);

  useEffect(() => {
    return () => {
      clearBusyCopilotFollowUps(chatId);
      clearUserMessagePlacements(chatId);
    };
  }, [chatId]);

  useEffect(() => {
    if (!chat) {
      return;
    }

    useChatInstancesStore.getState().setChatStatus(chatId, status);
  }, [chat, chatId, status]);

  const messagesRef = useRef(chatMessages);

  messagesRef.current = chatMessages;
  const chatHasActivity = hasChatActivity(chatMessages);
  const streamError = chatHasActivity ? error : undefined;
  const sendErrorRef = useRef<Error | undefined>(undefined);

  useEffect(() => {
    sendErrorRef.current = error;
  }, [error]);

  const {
    hasMore: hasMoreMessages,
    isLoadingMore: isLoadingMoreMessages,
    loadMore: loadMoreMessages,
    resetPagination: resetHistoryPagination,
  } = useChatInstanceHistoryPagination({
    chatId,
    hasChatInstance: Boolean(chat),
    conversationId,
    isLoadingHistoryPagination,
    storedPagination,
    suppressInitialHistoryMerge,
    chatMessages,
    setMessages,
    setHistoryPagination,
  });

  const abortInFlightTurnRef = useRef<(() => Promise<void>) | undefined>(
    undefined,
  );

  const {
    branchEditSource,
    inlineEdit,
    branchConfirmVariant,
    isBranchConfirmOpen,
    isApplyingBranch,
    requestBranchAskUserResend,
    confirmBranchResend,
    cancelBranchResend,
    handleBranchConfirmOpenChange,
    checkpoint,
    onEditUserMessage,
    onEditAskUserAnswer,
  } = useCopilotChatBranching({
    chatId,
    chatMessages,
    setMessages,
    onBranchApplied: resetHistoryPagination,
    abortInFlightTurn: async () => {
      await abortInFlightTurnRef.current?.();
    },
    sendInlineEdit: (args) => sendBranchInlineEdit(args),
  });

  const pendingHITLReview = useMemo(
    () => getPendingHITLReview(chatMessages),
    [chatMessages],
  );
  const isWaitingForHITLReview = Boolean(pendingHITLReview);

  const {
    isResumingStream,
    resumeActiveStream,
    cancelScheduledRecovery,
    sendGraphMessage,
    sendUserMessageWithResult,
    handleAbort,
    handleAbortSubagent,
    handleRegenerate,
    clearNeoResumeSuppression,
    clearComposerLoadingSuppression,
  } = useChatInstanceStreamActions({
    chatId,
    targetId,
    targetType,
    status,
    streamError,
    sendChatMessage,
    resumeStream,
    stop,
    regenerate,
    setMessages,
    messagesRef,
    sendErrorRef,
    intentionalAbortRef,
    pauseQueueDrainAfterAbortRef,
    chatComponentRef,
  });

  abortInFlightTurnRef.current = async () => {
    if (isActiveChatInstanceStatus(status)) {
      await handleAbort({ pauseQueueDrain: false });
    }
  };

  const {
    queuedMessages,
    sendQueuedMessageNow,
    clearBlockedAutoDrainForQueueItem,
  } = useChatInstanceQueueDrain({
    chatId,
    chatMessages,
    status,
    isResumingStream,
    isWaitingForHITLReview,
    sendGraphMessage,
    handleAbort,
    cancelScheduledRecovery,
    intentionalAbortRef,
    pauseQueueDrainAfterAbortRef,
    clearNeoResumeSuppression,
    clearComposerLoadingSuppression,
  });

  const performSendMessage = ({
    message,
    branchFromMessageId,
    activeBranchEditSource,
    inlinePositionedMentions,
  }: {
    message: TerraUIMessage;
    branchFromMessageId?: string;
    activeBranchEditSource?: NonNullable<typeof branchEditSource>;
    inlinePositionedMentions?: MentionEntityWithOffset[];
  }): Promise<boolean> =>
    performChatInstanceSendMessage({
      message,
      branchFromMessageId,
      activeBranchEditSource,
      inlinePositionedMentions,
      chatId,
      isApplyingBranch,
      isChatWindowActive,
      selectedMentions,
      clearMentions,
      getMentionsFromEditor,
      chatComponentRef,
      updateChatTab,
      skillDraftId,
      linkedObject: linkedObjectRef.current,
      tCopilot,
      chatMessages,
      displayedMessages,
      status,
      editingQueuedMessageId,
      updateQueuedMessage,
      clearBlockedAutoDrainForQueueItem,
      onQueuedMessageEditComplete,
      hasPendingHITLReview: Boolean(pendingHITLReview),
      enqueueMessage,
      handleAbort,
      setBusyFollowUps,
      pauseQueueDrainAfterAbortRef,
      cancelScheduledRecovery,
      intentionalAbortRef,
      getChatExecuteUrl: () => getChatExecuteUrlRef.current(),
      sendUserMessageWithResult,
      sendErrorRef,
      streamError,
      clearNeoResumeSuppression,
      clearComposerLoadingSuppression,
    });

  const handleSend = (message: TerraUIMessage): Promise<boolean> =>
    performSendMessage({ message });

  const sendBranchInlineEdit: SendBranchInlineEdit = ({
    edit,
    branchFromMessageId,
    branchEditSource: activeBranchEditSource,
  }) =>
    performSendMessage({
      message: {
        id: uuidv4(),
        role: 'user',
        parts: [{ type: 'text', text: edit.text }],
      },
      branchFromMessageId,
      activeBranchEditSource,
      inlinePositionedMentions: edit.positionedMentions,
    });

  return {
    messages: displayedMessages,
    sendMessage: sendGraphMessage,
    error: streamError,
    regenerate: handleRegenerate,
    resumeStream: resumeActiveStream,
    stop: handleAbort,
    abortSubagent: handleAbortSubagent,
    status,
    setMessages,
    handleSend,
    sendUserMessageWithResult,
    sendQueuedMessageNow,
    queuedMessages,
    isWaitingForHITLReview,
    isLoadingHistory,
    isHistoryReady: initialMessages !== null,
    historyLoadError,
    retryHistoryLoad,
    hasMoreMessages,
    isLoadingMoreMessages,
    loadMoreMessages,
    onEditUserMessage,
    onEditAskUserAnswer,
    branchEditSource,
    inlineEdit,
    checkpoint,
    branchConfirmVariant,
    requestBranchAskUserResend,
    isBranchConfirmOpen,
    isApplyingBranch,
    confirmBranchResend,
    cancelBranchResend,
    handleBranchConfirmOpenChange,
    isIntentionalAbort: () => intentionalAbortRef.current,
    isResumingStream,
  };
}
